// Sélecteur de langue de l'en-tête : un bouton + menu sur ordinateur (LanguageMenu), une ligne dans
// le tiroir du téléphone (LanguageList). Chaque langue est un VRAI lien (<a href hreflang lang>) :
// utilisable sans JavaScript, lisible par les moteurs de recherche, clic droit « ouvrir dans un nouvel
// onglet » possible. Un clic simple change la langue sans recharger la page.
// Rien ne s'affiche tant qu'une seule langue est active sur le site (REACT_APP_I18N_LANGS).
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import LanguageIcon from '@mui/icons-material/Language';
import { useLang, useT } from './hooks';
import { localizedPath } from './paths';

const useLanguageLinks = () => {
  const { lang, languages, setLanguage } = useLang();
  const location = useLocation(); // sans préfixe de langue : le routeur l'a retiré
  const hrefFor = (code) => localizedPath(location.pathname, code) + location.search + location.hash;
  return { lang, languages, setLanguage, hrefFor };
};

export function LanguageMenu() {
  const t = useT('common');
  const { lang, languages, setLanguage, hrefFor } = useLanguageLinks();
  const [anchor, setAnchor] = useState(null);
  if (languages.length < 2) return null;
  return (
    <>
      <Button
        color="inherit"
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        aria-label={t('common.language.label')}
        title={t('common.language.label')}
        startIcon={<LanguageIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ minWidth: 0 }}
      >
        {lang.toUpperCase()} ▾
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {languages.map((l) => (
          <MenuItem
            key={l.code}
            component="a"
            href={hrefFor(l.code)}
            hrefLang={l.code}
            lang={l.code}
            selected={l.code === lang}
            aria-current={l.code === lang ? 'true' : undefined}
            onClick={(e) => { e.preventDefault(); setAnchor(null); setLanguage(l.code); }}
          >
            {l.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function LanguageList() {
  const t = useT('common');
  const { lang, languages, setLanguage, hrefFor } = useLanguageLinks();
  if (languages.length < 2) return null;
  return (
    <ListItem sx={{ flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center' }} aria-label={t('common.language.label')}>
      <LanguageIcon fontSize="small" aria-hidden="true" />
      {languages.map((l) => (
        <Box
          key={l.code}
          component="a"
          href={hrefFor(l.code)}
          hrefLang={l.code}
          lang={l.code}
          aria-current={l.code === lang ? 'true' : undefined}
          onClick={(e) => { e.preventDefault(); setLanguage(l.code); }}
          sx={{ color: 'inherit', textDecoration: l.code === lang ? 'none' : 'underline', fontWeight: l.code === lang ? 700 : 400, padding: '6px 0' }}
        >
          {l.label}
        </Box>
      ))}
    </ListItem>
  );
}
